import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import NavBar from '../Navbar';
import { connect } from 'react-redux';
import { filter, get } from 'lodash';

import ConnectAsClientBanner from '../ConnectAsClientBanner';
import { onLogout } from '../../actions/auth';
import { logoutAsClient } from '../../actions/connectAsClient';
import styled from 'styled-components';
import Popup from '../Popup';
import Icon from '../Icon';

import {
  DISTRIBUTION,
  COMMUNICATION,
  PRICES,
  TRAVEL,
  NEGOCE,
  CARNETS_MARCHE,
  GLOBAL_DATA_VIEW
} from '../../constants/routes';

import { isUserServicesList } from '../../utils/routeAccess';
import { showDistributionAnalysis } from '../../utils/personalisation';
import { I18n } from 'react-redux-i18n';

const LEGAL_NOTICE_KEY = 'wineservices:legalNotice';

const LayoutContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 100vh;

  .contentContainer {
    display: flex;
    flex: 1;
    background-color: #eff0f2;
    padding-right: 0;
    padding-left: ${(props) => (props.legalNoticePopup ? '40px' : '90px')};

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      margin-top: 70px;
      padding-left: 0;
      padding-right: 0;
    }
  }
`;

const PopupContent = styled.div`
  .popupMessage {
    font-size: 14px;
    line-height: 1.8;
    width: 80%;
    margin-left: 10%;
    font-family: ${(props) => props.theme.fonts.sfcompact};
  }

  .popupLink {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    text-decoration: underline;
    margin-top: 40px;
  }

  .popupLink > *:not(:last-child) {
    margin-right: 20px;
  }

  .popupLink > a {
    font-family: ${(props) => props.theme.fonts.sfcompact};
    color: ${(props) => props.theme.colors.dark};
  }

  .popupLink:hover {
    opacity: 0.8;
    cursor: pointer;
  }
`;

class Layout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      legalNoticePopup: false
    };
  }

  getLegalNoticeSeen() {
    return JSON.parse(localStorage.getItem(LEGAL_NOTICE_KEY));
  }

  componentDidMount() {
    if (!this.state.legalNoticePopup) {
      let legalNoticeSeen = this.getLegalNoticeSeen();
      if (!legalNoticeSeen && window.location.pathname !== '/') {
        this.setState({ legalNoticePopup: true });
      }
    }
  }

  componentDidUpdate() {
    if (!this.state.legalNoticePopup) {
      let legalNoticeSeen = this.getLegalNoticeSeen();
      if (!legalNoticeSeen && window.location.pathname !== '/') {
        this.setState({ legalNoticePopup: true });
      }
    }
  }

  handleOk() {
    localStorage.setItem(LEGAL_NOTICE_KEY, JSON.stringify(true), {
      expires: 360
    });
    this.setState({ legalNoticePopup: false });
  }

  getData() {
    const { user } = this.props;
    const { services } = user;

    return [
      {
        title: I18n.t('navBar.dashboard'),
        shortTitle: I18n.t('navBar.dashboardShort'),
        icon: 'home',
        route: '/dashboard',
        enable:
          isUserServicesList(services, DISTRIBUTION.distribution) ||
          isUserServicesList(services, COMMUNICATION.communication) ||
          isUserServicesList(services, PRICES.prices_analysis) ||
          isUserServicesList(services, TRAVEL.travel)
      },
      {
        title: I18n.t('navBar.distribution'),
        shortTitle: I18n.t('navBar.distributionShort'),
        icon: 'distrib',
        route: '/distribution',
        subMenu: [
          {
            title: I18n.t('navBar.synthesis'),
            route: '/distribution/synthesis',
            enable: isUserServicesList(services, DISTRIBUTION.synthesis)
          },
          {
            title: user.client.displayWineshops
              ? I18n.t('navBar.restaurant')
              : I18n.t('restaurantsMaj'),
            route: '/distribution/restaurants',
            enable: isUserServicesList(services, DISTRIBUTION.restaurants_caves)
          },
          {
            title: I18n.t('navBar.globalView'),
            route: '/distribution/global-view',
            enable: isUserServicesList(
              services,
              GLOBAL_DATA_VIEW.global_data_view
            )
          },
          {
            title: I18n.t('navBar.primeur'),
            route: '/distribution/primeur',
            enable: isUserServicesList(services, DISTRIBUTION.primeur_campaign)
          },
          {
            title: I18n.t('navBar.auction'),
            route: '/distribution/auction',
            enable: isUserServicesList(services, DISTRIBUTION.auction_sales)
          },
          {
            title: I18n.t('navBar.onlineSalesPremium'),
            route: '/distribution/online-sales/overview',
            enable: isUserServicesList(services, DISTRIBUTION.online_sales)
          },
          {
            title: I18n.t('navBar.distributionAnalysis'),
            route: '/distribution/analysis',
            enable:
              showDistributionAnalysis(user.client.id) &&
              isUserServicesList(services, GLOBAL_DATA_VIEW.global_data_view)
          }
        ]
      },
      {
        title: 'My Performance',
        shortTitle: 'Perf.',
        icon: 'stats',
        route: '/performance',
        // Temporarily hidden from the menu (internal test) - page still reachable directly, see route.js
        enable: false,
        subMenu: [
          {
            title: 'Overview',
            route: '/performance',
            enable: false
          },
          {
            title: 'Restaurants',
            route: '/performance/restaurants',
            enable: false
          }
        ]
      },
      {
        title: I18n.t('navBar.notebooks'),
        shortTitle: I18n.t('navBar.notebooksShort'),
        route: '/notebooks',
        icon: 'folder',
        enable: isUserServicesList(services, CARNETS_MARCHE.carnets_marche)
      },
      {
        title: I18n.t('navBar.communication'),
        shortTitle: I18n.t('navBar.communicationShort'),
        icon: 'communication',
        route: '/communication',
        subMenu: [
          {
            title: I18n.t('navBar.media'),
            route: '/communication/media/',
            enable: isUserServicesList(services, COMMUNICATION.press)
          },
          {
            title: I18n.t('navBar.notes'),
            route: '/communication/notes',
            enable: isUserServicesList(services, COMMUNICATION.notes)
          }
        ]
      },
      {
        title: I18n.t('navBar.prices'),
        shortTitle: I18n.t('navBar.pricesShort'),
        icon: 'euro',
        route: '/prices',
        subMenu: [
          {
            title: I18n.t('navBar.price'),
            route: '/prices/price',
            enable: isUserServicesList(services, PRICES.prices_analysis)
          },
          {
            title: I18n.t('navBar.priceCompare'),
            route: '/prices/compare',
            enable: isUserServicesList(services, PRICES.prices_analysis)
          },
          {
            title: I18n.t('navBar.priceEvol'),
            route: '/prices/evolution',
            enable: isUserServicesList(services, PRICES.prices_analysis)
          }
        ]
      },
      {
        title: I18n.t('navBar.travel'),
        shortTitle: I18n.t('navBar.travelShort'),
        enable: isUserServicesList(services, TRAVEL.travel),
        icon: 'globe',
        route: '/travel'
      },
      {
        title: I18n.t('navBar.negoce'),
        shortTitle: I18n.t('navBar.negoceShort'),
        enable:
          isUserServicesList(services, NEGOCE.audit_de_place) ||
          isUserServicesList(services, NEGOCE.negociant),
        icon: 'stats',
        route: '/negociants-data'
      }
    ];
  }

  render() {
    const { legalNoticePopup } = this.state;
    const { auth, user } = this.props;

    const data = this.getData();

    const showingTabs = filter(
      data,
      (menu) =>
        menu.enable ||
        filter(
          get(menu, 'subMenu'),
          (submenu) => get(submenu, 'enable') === true
        ).length > 0
    );

    return (
      <LayoutContainer legalNoticePopup={legalNoticePopup}>
        {window.location.pathname !== '/' &&
          !window.location.pathname.startsWith('/mentions-legales') &&
          !window.location.pathname.startsWith('/password-reset/confirm') &&
          !legalNoticePopup && <NavBar data={showingTabs} expanded={false} />}
        <div className="contentContainer">
          {user.id !== null && auth && auth.clientToken && (
            <ConnectAsClientBanner
              onLogout={() => this.props.logoutAsClient()}
              name={`${user.firstname} ${user.lastname}`}
            />
          )}
          {this.props.children}
          {legalNoticePopup &&
            !window.location.pathname.startsWith('/mentions-legales') &&
            !window.location.pathname.startsWith('/password-reset/confirm') &&
            window.location.pathname !== '/' && (
              <Popup
                open={legalNoticePopup}
                title={I18n.t('legalNoticeTitle')}
                okLabel={I18n.t('legalNoticeAccept')}
                cancelLabel={I18n.t('legalNoticeRefuse')}
                onClose={() => {}}
                blockClose
                onCancel={() => {
                  this.setState({ legalNoticePopup: false });
                  this.props.logout();
                }}
                onOk={() => this.handleOk()}
              >
                <PopupContent>
                  <p className="popupMessage">{I18n.t('legalMentionPopup')}</p>
                  <br />
                  <p className="popupMessage">
                    {I18n.t('legalMentionPopupRefusal')}
                  </p>
                  <div className="popupLink">
                    <Icon icon="eye2" />
                    <a href="/mentions-legales" target="_blank">
                      {I18n.t('legalMentionPopupLink')}
                    </a>
                  </div>
                </PopupContent>
              </Popup>
            )}
        </div>
      </LayoutContainer>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    display_wineshops: get(state, 'auth.client.display_wineshops')
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => {
      dispatch(onLogout());
    },
    logoutAsClient: () => {
      dispatch(logoutAsClient());
    }
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Layout));
